import React from 'react';
import styled from 'styled-components';

const Section = styled.section`
  background: var(--cream);
`;

const HeroBlock = styled.div`
  position: relative;
  padding: 100px 20px 60px;
  text-align: center;
  background: var(--cream);
`;

const Label = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: var(--barn-red);
  margin-bottom: 15px;

  &::after {
    content: '';
    display: inline-block;
    width: 50px;
    height: 2px;
    background: var(--barn-red);
    margin-left: 12px;
    vertical-align: middle;
  }
`;

const Title = styled.h2`
  font-size: clamp(2.5rem, 6vw, 4rem);
  color: var(--earth);
  margin-bottom: 25px;
`;

const Description = styled.p`
  font-size: 1.15rem;
  color: var(--earth-light);
  max-width: 700px;
  margin: 0 auto 15px;
  line-height: 1.8;
`;

const Capacity = styled.p`
  font-size: 1rem;
  color: var(--barn-red);
  font-weight: 700;
  margin-bottom: 10px;
`;

const EventTypes = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  max-width: 700px;
  margin: 25px auto 0;
`;

const EventType = styled.div`
  padding: 8px 20px;
  background: rgba(139, 35, 35, 0.08);
  border: 1px solid rgba(139, 35, 35, 0.15);
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--earth);
  transition: all 0.3s ease;

  &:hover {
    background: var(--barn-red);
    color: var(--wheat);
    transform: translateY(-2px);
  }
`;

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: auto auto;
  gap: 0;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const PhotoCard = styled.div`
  position: relative;
  min-height: ${props => props.$tall ? '350px' : '250px'};
  overflow: hidden;

  ${props => props.$wide && `
    grid-column: span 2;

    @media (max-width: 500px) {
      grid-column: span 1;
    }
  `}
`;

const CardImg = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CardOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    180deg,
    rgba(60, 30, 20, 0.05) 0%,
    rgba(60, 30, 20, 0.65) 100%
  );
`;

const CardLabel = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  color: var(--wheat);
  font-weight: 700;
  font-size: 1.1rem;
  z-index: 1;
  text-shadow: 0 2px 8px rgba(0,0,0,0.4);
`;

const eventPhotos = [
  {
    label: 'Greenhouse Dinners',
    image: '/images/greenhouse-dinner-setup.jpg',
    wide: true,
    tall: true,
  },
  {
    label: 'Private Parties',
    image: '/images/greenhouse-table-flowers.jpg',
    tall: true,
  },
  {
    label: 'Baby Showers',
    image: '/images/baby-shower-setup.jpg',
    tall: true,
  },
  {
    label: 'Bridal Showers',
    image: '/images/wedding-rings-flower.jpg',
  },
  {
    label: 'Barn Events',
    image: '/images/barn-interior-buffet.jpg',
  },
  {
    label: 'Business Meetings',
    image: '/images/greenhouse-lounge.jpg',
  },
  {
    label: 'Birthdays',
    image: '/images/greenhouse-party-setup.jpg',
  },
];

export default function Events() {
  return (
    <Section id="events">
      <HeroBlock>
        <Label>Host Your Event</Label>
        <Title>Events</Title>
        <Description>
          Come celebrate with us for your next wedding, baby & bridal showers,
          birthdays, business meetings, remembrance of life service —
          any event from 20 to 100 people.
        </Description>
        <Capacity>Accommodating 20 - 100 Guests</Capacity>

        <EventTypes>
          <EventType>Weddings</EventType>
          <EventType>Rehearsal Dinners</EventType>
          <EventType>Baby Showers</EventType>
          <EventType>Bridal Showers</EventType>
          <EventType>Birthdays</EventType>
          <EventType>Business Meetings</EventType>
          <EventType>Private Parties</EventType>
          <EventType>Remembrance of Life</EventType>
        </EventTypes>
      </HeroBlock>

      <PhotoGrid>
        {eventPhotos.map((photo, i) => (
          <PhotoCard key={i} $wide={photo.wide} $tall={photo.tall}>
            <CardImg
              src={photo.image}
              alt={photo.label}
              loading="lazy"
              decoding="async"
            />
            <CardOverlay />
            <CardLabel>{photo.label}</CardLabel>
          </PhotoCard>
        ))}
      </PhotoGrid>
    </Section>
  );
}
